import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { decodeAccessClaims } from './jwtClaims';

type UserProfile = {
  id: string;
  email: string;
  displayName: string;
  role: string;
  /** Research-terminal tier (independent of role). Absent on legacy = 'none'. */
  researchTier?: string;
  twoFactorEnabled: boolean;
};

type AuthState = {
  user: UserProfile | null;
  accessToken: string | null;
  isAuthenticated: boolean;
  /** True while initAuth() is trying to refresh the session on page load. */
  isInitializing: boolean;
};

type AuthActions = {
  setAuth: (user: UserProfile, accessToken: string) => void;
  setAccessToken: (token: string) => void;
  logout: () => void;
  updateProfile: (updates: Partial<UserProfile>) => void;
};

export const useAuthStore = create<AuthState & AuthActions>()(
  persist(
    (set) => ({
      user: null,
      accessToken: null,
      isAuthenticated: false,
      isInitializing: true,

      setAuth: (user, accessToken) =>
        set({ user, accessToken, isAuthenticated: true, isInitializing: false }),

      setAccessToken: (token) =>
        set({ accessToken: token }),

      logout: () =>
        set({
          user: null,
          accessToken: null,
          isAuthenticated: false,
          isInitializing: false,
        }),

      updateProfile: (updates) =>
        set((state) => ({
          user: state.user ? { ...state.user, ...updates } : null,
        })),
    }),
    {
      name: 'lynx-auth',
      version: 3,
      migrate: (persisted) => {
        const state = persisted as Partial<AuthState & AuthActions>;
        return {
          user: state.user ?? null,
          // accessToken is intentionally NOT restored from storage
          accessToken: null,
          isAuthenticated: state.isAuthenticated ?? false,
          isInitializing: true,
        };
      },
      // Only persist user profile + auth flag — access token stays in memory only
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    },
  ),
);

/**
 * Get the current access token for API calls.
 * Returns null if not authenticated.
 */
export function getAccessToken(): string | null {
  return useAuthStore.getState().accessToken;
}

/**
 * On page load, attempt to refresh the access token using the HttpOnly
 * refresh cookie. If the cookie is expired/missing, clear auth state.
 * Call this once at app startup.
 */
/**
 * Dev/E2E test-mode check. True when `?test-auth=admin|user` is in the URL
 * AND the app is running in dev mode. Production builds strip this entirely.
 * When true, auth refresh + logout-on-401 are suppressed so tests stay signed in.
 */
export function isTestAuthActive(): boolean {
  if (!import.meta.env.DEV) return false;
  if (typeof window === 'undefined') return false;
  const ta = new URLSearchParams(window.location.search).get('test-auth');
  if (ta === 'admin' || ta === 'user') return true;
  // Also honor a sessionStorage flag so navigations without the query param keep the bypass.
  return sessionStorage.getItem('lynx-test-auth') !== null;
}

export async function initAuth(): Promise<void> {
  // Dev/E2E bypass — `?test-auth=admin|user` seeds an in-memory test user.
  // Only active in dev mode; production builds strip this block.
  if (import.meta.env.DEV) {
    const params = new URLSearchParams(window.location.search);
    const testAuth = params.get('test-auth');
    if (testAuth === 'admin' || testAuth === 'user') {
      sessionStorage.setItem('lynx-test-auth', testAuth);
      useAuthStore.getState().setAuth(
        {
          id: 'test-user',
          email: 'test@lynx.local',
          displayName: 'Test User',
          role: testAuth,
          // Dev bypass gets full research access so ?test-auth can reach /research.
          researchTier: 'research-trusted',
          twoFactorEnabled: false,
        },
        'test-access-token',
      );
      return;
    }
    // Restore from sessionStorage on reload/navigation inside the test session
    const stashed = sessionStorage.getItem('lynx-test-auth');
    if (stashed === 'admin' || stashed === 'user') {
      useAuthStore.getState().setAuth(
        {
          id: 'test-user',
          email: 'test@lynx.local',
          displayName: 'Test User',
          role: stashed,
          researchTier: 'research-trusted',
          twoFactorEnabled: false,
        },
        'test-access-token',
      );
      return;
    }
  }

  const { isAuthenticated } = useAuthStore.getState();

  // No persisted session — nothing to refresh
  if (!isAuthenticated) {
    useAuthStore.setState({ isInitializing: false });
    return;
  }

  try {
    const res = await fetch('/auth/refresh', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });

    if (res.ok) {
      const { accessToken } = (await res.json()) as { accessToken: string };
      useAuthStore.getState().setAccessToken(accessToken);
      // The refreshed token is authoritative — auth-service re-read the DB and
      // re-signed it, so it carries the user's CURRENT role + researchTier. Mirror
      // those claims into the store so guards reflect e.g. a just-granted research
      // tier on this page load, without forcing a re-login.
      const claims = decodeAccessClaims(accessToken);
      if (claims) useAuthStore.getState().updateProfile(claims);
      useAuthStore.setState({ isInitializing: false });
    } else {
      // Refresh cookie expired or invalid — force logout
      useAuthStore.getState().logout();
    }
  } catch {
    // Network error — clear auth state
    useAuthStore.getState().logout();
  }
}
